import DB from "./db.js";
import { buildMessageSearchText } from './messageSearch.js';
import { applyGroupMessageRemoval } from '../api/groupMessageCleanup.js';
const DB_USER_ID_REQUIRED_ERROR = 'DB_USER_ID_REQUIRED';

function normalizeDbUserId(userId) {
	if (userId === undefined || userId === null || userId === '') {
		return '';
	}
	return String(userId).trim();
}

/**
 * 内存版会话与消息存储，API 与 Dexie 版 indexDb 一致
 */
class ImMemoryDB extends DB {
	constructor() {
		super();
		this.configMap = new Map();
		this.conversationMap = new Map();
		this.conversationStateMap = new Map();
		this.messageMap = new Map();
		this.convMessageMap = new Map();
		this.friendMap = new Map();
		this.groupMap = new Map();
		this.groupMemberMap = new Map();
	}

	open(userId) {
		const normalizedUserId = normalizeDbUserId(userId);
		if (!normalizedUserId) {
			const error = new Error('打开本地数据库失败：userId 为空');
			error.code = DB_USER_ID_REQUIRED_ERROR;
			throw error;
		}
		this.userId = normalizedUserId;
		this.configMap = new Map();
		this.conversationMap = new Map();
		this.conversationStateMap = new Map();
		this.messageMap = new Map();
		this.convMessageMap = new Map();
		this.friendMap = new Map();
		this.groupMap = new Map();
		this.groupMemberMap = new Map();
	}

	close() {
		this.configMap = new Map();
		this.conversationMap = new Map();
		this.conversationStateMap = new Map();
		this.messageMap = new Map();
		this.convMessageMap = new Map();
		this.friendMap = new Map();
		this.groupMap = new Map();
		this.groupMemberMap = new Map();
		this.userId = null;
	}

	async loadAllConversations() {
		return Array.from(this.conversationMap.values());
	}

	async loadAllConversationStates() {
		return Array.from(this.conversationStateMap.values());
	}

	async deleteConversationByKey(convKey) {
		this.conversationMap.delete(convKey);
		const convMessages = this.convMessageMap.get(convKey);
		if (!convMessages) {
			return;
		}
		for (const localId of convMessages.keys()) {
			this.messageMap.delete(localId);
		}
		this.convMessageMap.delete(convKey);
	}

	async findConversationByKey(key) {
		return this.conversationMap.get(key);
	}

	async saveConversation(conversation) {
		this.conversationMap.set(conversation.key, conversation);
	}

	async saveConversationAndMessage(conversations, messages) {
		for (const c of conversations) {
			this.conversationMap.set(c.key, c);
		}
		for (const m of messages) {
			this.messageMap.set(m.localId, m);
			this._convMessageMap(m.convKey).set(m.localId, m);
		}
	}

	async deleteMessageByLocalId(localId) {
		const message = this.messageMap.get(localId);
		if (!message) {
			return;
		}
		this.messageMap.delete(localId);
		const convMessages = this.convMessageMap.get(message.convKey);
		if (!convMessages) {
			return;
		}
		convMessages.delete(localId);
		if (convMessages.size === 0) {
			this.convMessageMap.delete(message.convKey);
		}
	}

	async deleteMessageByConvKey(convKey) {
		const convMessages = this.convMessageMap.get(convKey);
		if (!convMessages) {
			return;
		}
		for (const localId of convMessages.keys()) {
			this.messageMap.delete(localId);
		}
		this.convMessageMap.delete(convKey);
	}

	async saveMessage(message) {
		const conversation = this.conversationMap.get(message.convKey);
		if (conversation && conversation.type === 'GROUP') {
			applyGroupMessageRemoval(message, conversation.messageRemovalCutoffs);
		}
		this.messageMap.set(message.localId, message);
		this._convMessageMap(message.convKey).set(message.localId, message);
	}

	async findMessageById(messageId) {
		for (const message of this.messageMap.values()) {
			if (message.id == messageId) {
				return message;
			}
		}
		return undefined;
	}

	async findMessageByLocalId(localId) {
		return this.messageMap.get(localId);
	}

	async findMessageByConvKey(convKey) {
		return this._convMessages(convKey);
	}

	async findRecentMessages(convKey, limit = 30) {
		const size = Math.max(1, limit || 30);
		return this._convMessages(convKey)
			.sort((a, b) => {
				const seqDiff = Number(a.seqNo || a.id || a.sendTime || 0) - Number(b.seqNo || b.id || b.sendTime || 0);
				if (seqDiff !== 0) {
					return seqDiff;
				}
				const sendTimeDiff = Number(a.sendTime || 0) - Number(b.sendTime || 0);
				if (sendTimeDiff !== 0) {
					return sendTimeDiff;
				}
				return String(a.localId || '').localeCompare(String(b.localId || ''));
			})
			.slice(-size);
	}

	async findMessagesBefore(convKey, beforeSeqNo, limit = 30) {
		const size = Math.max(1, limit || 30);
		const upperSeqNo = Number(beforeSeqNo);
		if (!Number.isFinite(upperSeqNo) || upperSeqNo <= 0) {
			return this.findRecentMessages(convKey, size);
		}
		return this._convMessages(convKey)
			.filter((m) => Number(m.seqNo || m.id || m.sendTime || 0) < upperSeqNo)
			.sort((a, b) => {
				const seqDiff = Number(a.seqNo || a.id || a.sendTime || 0) - Number(b.seqNo || b.id || b.sendTime || 0);
				if (seqDiff !== 0) {
					return seqDiff;
				}
				const idDiff = Number(a.id || 0) - Number(b.id || 0);
				if (idDiff !== 0) {
					return idDiff;
				}
				const sendTimeDiff = Number(a.sendTime || 0) - Number(b.sendTime || 0);
				if (sendTimeDiff !== 0) {
					return sendTimeDiff;
				}
				return String(a.localId || '').localeCompare(String(b.localId || ''));
			})
			.slice(-size);
	}

	async findMessagesByTypes(convKey, types, beforeSeqNo, limit = 30, keyword = '') {
		const typeSet = new Set((Array.isArray(types) ? types : []).map(Number));
		const before = Number(beforeSeqNo);
		const size = Math.max(1, Math.floor(Number(limit) || 30));
		const normalizedKeyword = String(keyword || '').trim().toLowerCase();
		return this._convMessages(convKey)
			.filter(message => {
				const seqNo = Number(message && message.seqNo);
				return Number.isFinite(seqNo) && seqNo > 0 &&
					(!Number.isFinite(before) || before <= 0 || seqNo < before) &&
					(!typeSet.size || typeSet.has(Number(message.type))) &&
					(!normalizedKeyword || buildMessageSearchText(message).toLowerCase().includes(normalizedKeyword));
			})
			.sort((left, right) => {
				const seqDiff = Number(right.seqNo) - Number(left.seqNo);
				if (seqDiff) return seqDiff;
				const timeDiff = Number(right.sendTime || 0) - Number(left.sendTime || 0);
				if (timeDiff) return timeDiff;
				return String(right.localId || '').localeCompare(String(left.localId || ''));
			})
			.slice(0, size);
	}

	async upsertChatSnapshot(snapshot) {
		const conversation = snapshot && snapshot.conversation;
		if (!conversation || !conversation.key) {
			return;
		}
		this.conversationMap.set(conversation.key, conversation);
		const messages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
		for (const m of messages) {
			this.messageMap.set(m.localId, m);
			this._convMessageMap(m.convKey).set(m.localId, m);
		}
	}

	async persistChatMutations(payload = {}) {
		const clearedConversationKeys = Array.isArray(payload.clearedConversationKeys) ? payload.clearedConversationKeys : [];
		const deletedLocalIds = Array.isArray(payload.deletedLocalIds) ? payload.deletedLocalIds : [];
		const deletedConversationKeys = Array.isArray(payload.deletedConversationKeys) ? payload.deletedConversationKeys : [];
		const snapshots = Array.isArray(payload.snapshots) ? payload.snapshots : [];
		const conversationStates = Array.isArray(payload.conversationStates) ? payload.conversationStates : [];
		const configs = Array.isArray(payload.configs) ? payload.configs : [];
		if (payload.replaceConversationStates === true) {
			this.conversationStateMap = new Map();
		}
		for (const convKey of clearedConversationKeys) {
			await this.deleteMessageByConvKey(convKey);
		}
		for (const localId of deletedLocalIds) {
			await this.deleteMessageByLocalId(localId);
		}
		for (const convKey of deletedConversationKeys) {
			await this.deleteConversationByKey(convKey);
		}
		for (const snapshot of snapshots) {
			const conversation = snapshot && snapshot.conversation;
			if (conversation && conversation.type === 'GROUP') {
				const cutoffs = conversation.messageRemovalCutoffs || {};
				(snapshot.messages || []).forEach(message => applyGroupMessageRemoval(message, cutoffs));
				this._convMessages(conversation.key).forEach(message => applyGroupMessageRemoval(message, cutoffs));
			}
			await this.upsertChatSnapshot(snapshot);
		}
		conversationStates.forEach((state) => {
			if (state && state.key) {
				this.conversationStateMap.set(state.key, state);
			}
		});
		configs.forEach((config) => {
			if (config && config.key) {
				this.configMap.set(config.key, config.val);
			}
		});
	}

	async findPageMessage(convKey, minSeqNo, maxSeqNo) {
		return this._convMessages(convKey)
			.filter((m) => m.seqNo >= minSeqNo && m.seqNo <= maxSeqNo)
			.sort((a, b) => {
				if (a.seqNo !== b.seqNo) {
					return a.seqNo - b.seqNo;
				}
				return a.sendTime - b.sendTime;
			});
	}

	async findQuoteMessages(message) {
		return this._convMessages(message.convKey).filter((m) => {
			return m.quoteMessage && m.quoteMessage.id == message.id;
		});
	}

	async findAllFriends() {
		return Array.from(this.friendMap.values());
	}

	async saveFriends(friends) {
		(Array.isArray(friends) ? friends : []).forEach((friend) => this.saveFriend(friend));
	}

	async saveFriend(friend) {
		if (friend && friend.id !== undefined && friend.id !== null) {
			this.friendMap.set(friend.id, friend);
		}
	}

	async syncAllFriends(friends) {
		this.configMap.set('lastSyncFriendTime', new Date().getTime());
		this.friendMap = new Map();
		await this.saveFriends(friends);
	}

	async findLastSyncFriendsTime(friends) {
		return this.configMap.get('lastSyncFriendTime') || 0;
	}

	async findAllGroups() {
		return Array.from(this.groupMap.values());
	}

	async saveGroups(groups) {
		(Array.isArray(groups) ? groups : []).forEach((group) => this.saveGroup(group));
	}

	async saveGroup(group) {
		if (group && group.id !== undefined && group.id !== null) {
			this.groupMap.set(group.id, group);
		}
	}

	async syncAllGroups(groups) {
		this.configMap.set('lastSyncGroupTime', new Date().getTime());
		this.groupMap = new Map();
		await this.saveGroups(groups);
	}

	async findLastSyncGroupsTime(groups) {
		return this.configMap.get('lastSyncGroupTime') || 0;
	}

	_groupMemberMap(groupId) {
		const groupKey = String(groupId);
		let members = this.groupMemberMap.get(groupKey);
		if (!members) {
			members = new Map();
			this.groupMemberMap.set(groupKey, members);
		}
		return members;
	}

	normalizeGroupMemberRecord(groupId, member) {
		if (!member || member.userId === undefined || member.userId === null) {
			return null;
		}
		const record = { ...member };
		record.groupId = groupId;
		record.snapshotAt = Date.now();
		delete record.checked;
		delete record.locked;
		delete record.hide;
		delete record.online;
		return record;
	}

	async findGroupMembers(groupId) {
		return Array.from(this._groupMemberMap(groupId).values());
	}

	async saveGroupMembers(groupId, members) {
		const memberMap = this._groupMemberMap(groupId);
		(Array.isArray(members) ? members : []).forEach((member) => {
			const record = this.normalizeGroupMemberRecord(groupId, member);
			if (record) {
				memberMap.set(record.userId, record);
			}
		});
	}

	async syncGroupMembers(groupId, members) {
		const groupKey = String(groupId);
		this.groupMemberMap.set(groupKey, new Map());
		await this.saveGroupMembers(groupId, members);
		this.configMap.set(`lastSyncGroupMembersTime:${groupId}`, new Date().getTime());
	}

	async deleteGroupMembers(groupId) {
		this.groupMemberMap.delete(String(groupId));
	}

	async saveConfig(key, val) {
		this.configMap.set(key, val);
	}

	async findConfig(key) {
		return this.configMap.get(key);
	}

	async deleteConfig(key) {
		this.configMap.delete(key);
	}

	async clearLocalCache() {
		this.configMap = new Map();
		this.conversationMap = new Map();
		this.conversationStateMap = new Map();
		this.messageMap = new Map();
		this.convMessageMap = new Map();
		this.friendMap = new Map();
		this.groupMap = new Map();
		this.groupMemberMap = new Map();
	}

	_convMessageMap(convKey) {
		let convMessages = this.convMessageMap.get(convKey);
		if (!convMessages) {
			convMessages = new Map();
			this.convMessageMap.set(convKey, convMessages);
		}
		return convMessages;
	}

	_convMessages(convKey) {
		const convMessages = this.convMessageMap.get(convKey);
		return convMessages ? Array.from(convMessages.values()) : [];
	}
}

export default ImMemoryDB;
